import { useLazyQuery, useQuery } from '@apollo/client';
import { IToken } from '@dokucoach/common-types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { GetUserByToken, GET_USER_BY_TOKEN, TimeosConfig, User } from 'services/auth';
import useToken from 'utils/token';
import { useLogout } from './useLogout';

type AuthType = {
  isLoggedIn: boolean;
  setIsLoggedIn: Dispatch<SetStateAction<boolean>>;
  fetchUser: (token: IToken) => void;
  user: User | null;
  timeosConfig: TimeosConfig | null;
  isLoading: boolean;
  logout: () => void;
};

/**
 * DO NOT call useAuth() in any place other than the AuthManager!
 * If you need any value or function from this hook, get it though the context
 * by using the useContextAuthManager() hook!
 */
export const useAuth = (): AuthType => {
  const {
    token: { refreshToken },
  } = useToken();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(!!refreshToken);
  const [user, setUser] = useState<User | null>(null);
  const [timeosConfig, setTimeosConfig] = useState<TimeosConfig | null>(null);
  const logout = useLogout();

  const [getUserByToken, { data, error, loading: isLoading }] =
    useLazyQuery<GetUserByToken>(GET_USER_BY_TOKEN);
  const { data: initialData, error: initialError } = useQuery<GetUserByToken>(GET_USER_BY_TOKEN, {
    skip: !refreshToken,
    variables: { token: refreshToken },
  });
  const fetchUser = async (token: IToken) => {
    if (!token?.refreshToken) return;
    try {
      await getUserByToken({ variables: { token: token.refreshToken } });
    } catch (e) {
      await logout();
    }
  };

  useEffect(() => {
    if (error || initialError) {
      setIsLoggedIn(false);
    }

    if (data?.getUserByToken) {
      const { getUserByToken, timeosUser, timeosCustomerId } = data;
      setUser({ ...getUserByToken });
      setTimeosConfig({ timeosUser, timeosCustomerId });
      setIsLoggedIn(true);
    } else if (initialData?.getUserByToken) {
      const { getUserByToken, timeosUser, timeosCustomerId } = initialData;
      setUser({ ...getUserByToken });
      setTimeosConfig({ timeosUser, timeosCustomerId });
      setIsLoggedIn(true);
    }
  }, [initialData, data, initialError, error]);

  useEffect(() => {
    if (!isLoggedIn && user) {
      setUser(null);
    }
  }, [isLoggedIn, user]);

  return {
    isLoggedIn,
    setIsLoggedIn,
    fetchUser,
    user,
    timeosConfig,
    isLoading,
    logout,
  };
};
